import React, { useState, useEffect, useCallback } from 'react';
import { Play, Loader, CheckCircle, AlertCircle, Monitor } from 'lucide-react';
import { gamingAPI } from '../services/api';

const AutomatedGaming = ({ session, onClose }) => {
  const [status, setStatus] = useState('initializing');
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);
  // const [moonlightReady, setMoonlightReady] = useState(false);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  };

  const checkInstanceStatus = useCallback(async () => {
    try {
      const response = await gamingAPI.getSessionDetails(session.id);
      const instanceStatus = response.vast_instance_status;
      
      if (instanceStatus?.status === 'running') {
        addLog('✅ GPU instance is running', 'success');
        setStatus('instance_ready');
        setProgress(30);
        return { ready: true, sessionDetails: response };
      } else if (instanceStatus?.status === 'starting') {
        addLog('⏳ Instance is starting up...', 'info');
        setProgress(20);
        return { ready: false, sessionDetails: response };
      } else {
        addLog('❌ Instance failed to start', 'error');
        setStatus('failed');
        return { ready: false, sessionDetails: response };
      }
    } catch (error) {
      addLog(`❌ Error checking instance: ${error.message}`, 'error');
      return { ready: false, sessionDetails: null };
    }
  }, [session.id]);

  const checkMoonlightInstallation = useCallback(async () => {
    // Check if Moonlight is installed
    if (navigator.userAgent.includes('Mac')) {
      // Check for macOS app
      return false; // For now, always download
    } else if (navigator.userAgent.includes('Windows')) {
      // Check for Windows executable
      return false;
    } else {
      // Check for Linux AppImage
      return false;
    }
  }, []);

  const showMoonlightInstructions = useCallback(async () => {
    addLog('📥 Please download Moonlight manually from the dashboard', 'info');
    addLog('🔗 Visit the Moonlight Download section for your platform', 'info');
    setStatus('moonlight_required');
  }, []);

  const setupSSHTunnel = useCallback(async (sessionDetails) => {
    addLog('🔗 Setting up SSH tunnel...', 'info');
    
    try {
      // Get SSH command from session details
      const sshCommandData = sessionDetails.ssh_command;
      if (!sshCommandData) {
        throw new Error('No SSH command available');
      }
      
      // Handle both old string format and new object format
      let sshCommand;
      if (typeof sshCommandData === 'string') {
        sshCommand = sshCommandData;
      } else {
        // Use the recommended command (from ssh_keys directory)
        sshCommand = sshCommandData.recommended || sshCommandData.from_ssh_keys;
      }
      
      addLog('📋 SSH Commands Available:', 'info');
      if (typeof sshCommandData === 'object') {
        addLog(`   From ssh_keys dir: ${sshCommandData.from_ssh_keys}`, 'info');
        addLog(`   From project root: ${sshCommandData.from_root}`, 'info');
        addLog(`   SSH: ${sshCommandData.vast_style}`, 'info');
      }
      
      addLog('🚀 Manual SSH Tunnel Setup Required:', 'info');
      addLog('📋 Please run this SSH command in your terminal:', 'info');
      addLog(`   ${sshCommand}`, 'info');
      addLog('⏳ Click "Continue" when the SSH tunnel is established...', 'info');
      
      // Show manual wait dialog
      setStatus('waiting_for_manual_ssh');
      
      // Return a promise that resolves when user clicks continue
      return new Promise((resolve) => {
        // Store the resolve function to be called when user clicks continue
        window.manualSSHResolve = resolve;
      });
      
    } catch (error) {
      addLog(`❌ SSH tunnel setup failed: ${error.message}`, 'error');
      return false;
    }
  }, []);

  const launchMoonlight = useCallback(async (sessionDetails) => {
    addLog('🚀 Launching Moonlight with pre-configured settings...', 'info');
    
    // First, set up SSH tunnel
    const tunnelReady = await setupSSHTunnel(sessionDetails);
    if (!tunnelReady) {
      addLog('⚠️ SSH tunnel not ready, but continuing with Moonlight launch...', 'warning');
    }
    
    // Create Moonlight launch command
    const moonlightCommand = {
      host: 'localhost:47990',
      username: 'sunshine',
      password: '',
      pin: '1234',
      app: 'Steam'
    };

    // Store connection details for Moonlight
    localStorage.setItem('moonlight_connection', JSON.stringify(moonlightCommand));
    
    // Show connection details instead of auto-launching
    addLog('📋 Moonlight connection details ready!', 'success');
    addLog('💡 Click the "Launch Moonlight" button below to connect', 'info');
    addLog(`Connection details: ${JSON.stringify(moonlightCommand, null, 2)}`, 'info');
  }, [setupSSHTunnel]);

  const startMoonlightClient = useCallback(async (sessionDetails) => {
    try {
      addLog('🎮 Starting Moonlight client...', 'info');
      setStatus('launching_moonlight');
      setProgress(80);

      // Check if Moonlight is installed
      const isInstalled = await checkMoonlightInstallation();
      
      if (!isInstalled) {
        addLog('📱 Moonlight not found, please install manually...', 'info');
        await showMoonlightInstructions();
        return;
      }

      // Launch Moonlight with pre-configured settings
      await launchMoonlight(sessionDetails);
      
      addLog('✅ Moonlight launched successfully!', 'success');
      setStatus('ready');
      setProgress(100);
      // setMoonlightReady(true);

    } catch (error) {
      addLog(`❌ Moonlight launch failed: ${error.message}`, 'error');
      setStatus('failed');
    }
  }, [checkMoonlightInstallation, showMoonlightInstructions, launchMoonlight]);

  const establishWebRTCConnection = useCallback(async (sessionDetails) => {
    try {
      addLog('🌐 Establishing WebRTC connection...', 'info');
      setStatus('connecting');
      setProgress(50);

      // Create WebRTC peer connection
      const peerConnection = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });

      // Set up data channel for tunnel
      const dataChannel = peerConnection.createDataChannel('tunnel', {
        ordered: true
      });

      dataChannel.onopen = () => {
        addLog('✅ WebRTC tunnel established', 'success');
        setStatus('tunnel_ready');
        setProgress(70);
        startMoonlightClient(sessionDetails);
      };

      dataChannel.onerror = (error) => {
        addLog(`❌ WebRTC error: ${error}`, 'error');
        setStatus('failed');
      };

      // For now, simulate WebRTC connection
      // In production, this would connect to a WebRTC server
      setTimeout(() => {
        addLog('✅ WebRTC tunnel established (simulated)', 'success');
        setStatus('tunnel_ready');
        setProgress(70);
        startMoonlightClient(sessionDetails);
      }, 2000);

    } catch (error) {
      addLog(`❌ WebRTC connection failed: ${error.message}`, 'error');
      setStatus('failed');
    }
  }, [startMoonlightClient]);

  const startAutomatedProcess = useCallback(async () => {
    addLog('🎮 Starting automated gaming setup...', 'info');
    setStatus('starting');
    setProgress(10);

    // Step 1: Check instance status
    const instanceResult = await checkInstanceStatus();
    if (!instanceResult.ready) {
      // Poll for instance readiness
      const pollInterval = setInterval(async () => {
        const result = await checkInstanceStatus();
        if (result.ready) {
          clearInterval(pollInterval);
          establishWebRTCConnection(result.sessionDetails);
        }
      }, 5000);
      return;
    }

    // Step 2: Establish connection
    await establishWebRTCConnection(instanceResult.sessionDetails);
  }, [checkInstanceStatus, establishWebRTCConnection]);

  useEffect(() => {
    if (session && status === 'initializing') {
      startAutomatedProcess();
    }
  }, [session, status, startAutomatedProcess]);

  const getStatusIcon = () => {
    switch (status) {
      case 'initializing':
      case 'starting':
        return <Loader className="w-6 h-6 animate-spin text-blue-500" />;
      case 'instance_ready':
      case 'connecting':
      case 'tunnel_ready':
      case 'launching_moonlight':
        return <Loader className="w-6 h-6 animate-spin text-yellow-500" />;
      case 'ready':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-6 h-6 text-red-500" />;
      default:
        return <Monitor className="w-6 h-6 text-gray-500" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'initializing': return 'Initializing...';
      case 'starting': return 'Starting instance...';
      case 'instance_ready': return 'Instance ready, connecting...';
      case 'connecting': return 'Establishing connection...';
      case 'tunnel_ready': return 'Tunnel ready, launching Moonlight...';
      case 'launching_moonlight': return 'Starting Moonlight client...';
      case 'waiting_for_installation': return 'Waiting for Moonlight installation...';
      case 'waiting_for_manual_ssh': return 'Waiting for manual SSH tunnel...';
      case 'ready': return 'Ready to play!';
      case 'failed': return 'Setup failed';
      default: return 'Unknown status';
    }
  };

  const handleManualSSHContinue = () => {
    if (window.manualSSHResolve) {
      addLog('✅ Continuing with SSH tunnel established...', 'success');
      window.manualSSHResolve(true);
      window.manualSSHResolve = null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center">
            {getStatusIcon()}
            <span className="ml-3">Automated Gaming Setup</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ×
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Status */}
        <div className="mb-6">
          <div className="text-lg font-semibold text-white mb-2">
            {getStatusText()}
          </div>
          <div className="text-sm text-gray-400">
            {status === 'ready' ? 'Your gaming session is ready!' : 'Please wait while we set everything up...'}
          </div>
        </div>

        {/* Logs */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">Setup Log</h3>
          <div className="bg-black rounded-lg p-4 h-48 overflow-y-auto">
            {logs.map((log, index) => (
              <div key={index} className={`text-sm mb-1 ${
                log.type === 'success' ? 'text-green-400' :
                log.type === 'error' ? 'text-red-400' :
                log.type === 'warning' ? 'text-yellow-400' :
                'text-gray-300'
              }`}>
                <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          {status === 'ready' && (
            <button
              onClick={() => window.open('moonlight://connect', '_blank')}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg flex items-center"
            >
              <Play className="w-4 h-4 mr-2" />
              Launch Game
            </button>
          )}
          
          {status === 'failed' && (
            <button
              onClick={startAutomatedProcess}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
            >
              Retry Setup
            </button>
          )}

          {status === 'waiting_for_manual_ssh' && (
            <button
              onClick={handleManualSSHContinue}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg"
            >
              Continue (SSH Tunnel Ready)
            </button>
          )}
          
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutomatedGaming;
